// Analytics tracking service for SmartReadGo
import { v4 as uuidv4 } from 'uuid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GUEST_ID_KEY = 'smartreadgo_guest_id';
const SESSION_ID_KEY = 'smartreadgo_session_id';
const SESSION_START_KEY = 'smartreadgo_session_start';
const FIRST_VISIT_KEY = 'smartreadgo_first_visit';

// Get or create guest ID
export const getGuestId = () => {
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  if (!guestId) {
    guestId = `guest_${uuidv4()}`;
    localStorage.setItem(GUEST_ID_KEY, guestId);
  }
  return guestId;
};

// Get current user ID (from localStorage auth)
export const getCurrentUserId = () => {
  const userProfile = localStorage.getItem('smartreadgo_user_profile');
  if (userProfile) {
    try {
      const profile = JSON.parse(userProfile);
      return profile.email || null;
    } catch {
      return null;
    }
  }
  return null;
};

// Check if user is logged in
export const isLoggedIn = () => {
  return localStorage.getItem('smartreadgo_auth_token') !== null;
};

// Check if this is first visit
export const isFirstVisit = () => {
  return localStorage.getItem(FIRST_VISIT_KEY) === null;
};

// Mark as visited
export const markAsVisited = () => {
  localStorage.setItem(FIRST_VISIT_KEY, 'true');
};

// Get or create session ID
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${uuidv4()}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
  }
  return sessionId;
};

// Get session duration in seconds
export const getSessionDuration = () => {
  const startTime = sessionStorage.getItem(SESSION_START_KEY);
  if (startTime) {
    return Math.floor((Date.now() - parseInt(startTime)) / 1000);
  }
  return 0;
};

// Track an event
export const trackEvent = async (eventType, data = {}) => {
  try {
    const userId = getCurrentUserId();
    const guestId = !userId ? getGuestId() : null;
    
    const eventData = {
      event_type: eventType,
      user_id: userId,
      guest_id: guestId,
      ...data
    };

    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

// Track page view
export const trackPageView = (page) => {
  trackEvent('page_view', { page });
};

// Track category selection
export const trackCategorySelect = (category) => {
  trackEvent('category_select', { category });
};

// Track publisher selection
export const trackPublisherSelect = (publisher) => {
  trackEvent('publisher_select', { publisher });
};

// Track audio play
export const trackAudioPlay = (articleId, articleTitle) => {
  trackEvent('audio_play', { 
    article_id: articleId, 
    article_title: articleTitle 
  });
};

// Track article view
export const trackArticleView = (articleId, articleTitle, category) => {
  trackEvent('article_view', { 
    article_id: articleId, 
    article_title: articleTitle,
    category 
  });
};

// Session tracking
export const trackSessionStart = async () => {
  try {
    const userId = getCurrentUserId();
    const guestId = !userId ? getGuestId() : null;
    const sessionId = getSessionId();

    await fetch(`${BACKEND_URL}/api/analytics/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user_id: userId,
        guest_id: guestId,
        session_id: sessionId,
        event_type: 'session_start'
      })
    });
  } catch (error) {
    console.error('Session start tracking error:', error);
  }
};

export const trackSessionEnd = async () => {
  try {
    const userId = getCurrentUserId();
    const guestId = !userId ? getGuestId() : null;
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    const duration = getSessionDuration();

    if (sessionId) {
      await fetch(`${BACKEND_URL}/api/analytics/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          session_id: sessionId,
          event_type: 'session_end',
          duration_seconds: duration
        })
      });
    }
  } catch (error) {
    console.error('Session end tracking error:', error);
  }
};

// Heartbeat to track active time
export const trackSessionHeartbeat = async () => {
  try {
    const userId = getCurrentUserId();
    const guestId = !userId ? getGuestId() : null;
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);

    if (sessionId) {
      await fetch(`${BACKEND_URL}/api/analytics/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          guest_id: guestId,
          session_id: sessionId,
          event_type: 'session_heartbeat'
        })
      });
    }
  } catch (error) {
    console.error('Session heartbeat error:', error);
  }
};

// Initialize analytics (call on app start)
export const initAnalytics = () => {
  // Start session
  trackSessionStart();
  
  // Set up heartbeat every 30 seconds
  setInterval(trackSessionHeartbeat, 30000);
  
  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    trackSessionEnd();
  });
  
  // Track session end on visibility change (app backgrounded)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackSessionEnd();
    } else if (document.visibilityState === 'visible') {
      trackSessionStart();
    }
  });
};
